/**
 * Toast Notification System
 *
 * Provides toast notifications and confirmation dialogs to replace
 * window.alert() and window.confirm().
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import './Toast.css';

// ============================================================================
// Types
// ============================================================================

type ToastType = 'info' | 'success' | 'warning' | 'error';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface ToastContextValue {
  /** Show a toast notification */
  toast: (message: string, type?: ToastType) => void;
  /** Show a confirmation dialog, returns true if confirmed */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

// ============================================================================
// Context
// ============================================================================

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmState, setConfirmState] = useState<{
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const toast = useCallback((message: string, type: ToastType = 'info') => {
    const id = crypto.randomUUID();
    setToasts(prev => [...prev, { id, message, type }]);

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise(resolve => {
      setConfirmState({ options, resolve });
    });
  }, []);

  const handleConfirm = useCallback((result: boolean) => {
    if (confirmState) {
      confirmState.resolve(result);
      setConfirmState(null);
    }
  }, [confirmState]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast, confirm }}>
      {children}

      {/* Toast container */}
      {toasts.length > 0 && (
        <div className="toast-container">
          {toasts.map(t => (
            <div
              key={t.id}
              className={`toast toast-${t.type}`}
              onClick={() => dismissToast(t.id)}
            >
              <span className="toast-icon">{getIcon(t.type)}</span>
              <span className="toast-message">{t.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation dialog */}
      {confirmState && (
        <div className="confirm-overlay" onClick={() => handleConfirm(false)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            {confirmState.options.title && (
              <div className="confirm-title">{confirmState.options.title}</div>
            )}
            <div className="confirm-message">{confirmState.options.message}</div>
            <div className="confirm-actions">
              <button
                className="confirm-btn confirm-cancel"
                onClick={() => handleConfirm(false)}
              >
                {confirmState.options.cancelLabel || 'Cancel'}
              </button>
              <button
                className={`confirm-btn confirm-ok ${confirmState.options.danger ? 'danger' : ''}`}
                onClick={() => handleConfirm(true)}
                autoFocus
              >
                {confirmState.options.confirmLabel || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function getIcon(type: ToastType): string {
  switch (type) {
    case 'success': return '\u2713'; // checkmark
    case 'warning': return '\u26A0'; // warning sign
    case 'error': return '\u2717';   // x mark
    default: return '\u2139';        // info
  }
}
